import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { User } from "@/types/userTypes";
import io, { Socket } from "socket.io-client";
import LiveMap from "./LiveMap";
import { api } from "@/utils/api";
import { useTranslation } from "react-i18next";
import { formatDistanceMeters, geoJsonToPoint, type GeoPoint } from "@/utils/geo";
import { useGeolocationWatch } from "@/hooks/useGeolocationWatch";

export type Location = {
  lat: number;
  lng: number;
};

type Props = {
  userId: string;
  jobLocation: {
    type: string;
    coordinates: [number, number]; // [lng, lat]
    value?: string;
  };
  jobId: string;
  distanceMeters?: number;
  autoArrivalEnabled?: boolean;
  /** Reuse GPS from page-level hook to avoid duplicate geolocation requests */
  workerPosition?: GeoPoint | null;
  /** Page-level socket broadcast active — worker map is display-only */
  pageLevelBroadcast?: boolean;
};

const backendUrl = import.meta.env.VITE_BACKEND_URL;

const LiveLocation = ({
  userId,
  jobLocation,
  jobId,
  distanceMeters,
  autoArrivalEnabled,
  workerPosition: workerPositionProp,
  pageLevelBroadcast = false,
}: Props) => {
  const [lastLocationTimestamp, setLastLocationTimestamp] = useState<number | null>(null);
  const [locationSaved, setLocationSaved] = useState(false);
  const [location, setLocation] = useState<Location | null>(null);
  const [myLocation, setMyLocation] = useState<Location | null>(null);
  const [socketStatus, setSocketStatus] = useState<string>("disconnected");
  const [mylocationimg, setMyLocationimg] = useState("");
  const [otherlocationimg, setotherLocationimg] = useState("");
  const hasLiveLocationRef = useRef(false);
  const initialWorkerLocationSentRef = useRef(false);
  const { t } = useTranslation();

  const { user: me }: { user: User } = useSelector(
    (state: { user }) => state.user
  );

  const isWorker = me.type === "worker";
  const usePageBroadcast = isWorker && pageLevelBroadcast;
  const clientId = isWorker ? userId : me._id;
  const professionalId = isWorker ? me._id : userId;
  const room = `${clientId}-${professionalId}`;

  const jobPoint = geoJsonToPoint(jobLocation?.coordinates);

  const { position: localWorkerPosition } = useGeolocationWatch(
    isWorker && workerPositionProp === undefined
  );

  const activeWorkerPosition =
    workerPositionProp !== undefined ? workerPositionProp : localWorkerPosition;

  const [socket] = useState<Socket>(() =>
    io(backendUrl || "http://localhost:4000", {
      transports: ["websocket"],
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    })
  );

  // Worker: job site is always the "other" pin on the map
  useEffect(() => {
    if (!isWorker || !jobPoint) return;
    setMyLocationimg("/images/worker_.png");
    setotherLocationimg("/images/house.png");
    setLocation(jobPoint);
  }, [isWorker, jobPoint?.lat, jobPoint?.lng]);

  // Worker: sync provider pin from shared or local GPS stream
  useEffect(() => {
    if (!isWorker || !activeWorkerPosition) return;
    setMyLocation(activeWorkerPosition);
  }, [isWorker, activeWorkerPosition?.lat, activeWorkerPosition?.lng]);

  // Worker: persist initial location once when not using page-level broadcast
  useEffect(() => {
    if (usePageBroadcast || !isWorker || !activeWorkerPosition || locationSaved) return;

    const workerId = me._id;
    api
      .saveWorkerInitialLocation({
        jobId,
        workerId,
        location: {
          latitude: activeWorkerPosition.lat,
          longitude: activeWorkerPosition.lng,
        },
      })
      .then((response) => {
        if (response?.success) {
          setLocationSaved(true);
        }
      })
      .catch((err) => console.error("Failed to save initial location", err));
  }, [usePageBroadcast, isWorker, activeWorkerPosition, locationSaved, jobId, me._id]);

  // Client: poll REST fallback when socket updates are stale
  useEffect(() => {
    if (isWorker) return;

    const fallbackTimeout = setInterval(() => {
      const now = Date.now();
      const stale = lastLocationTimestamp && now - lastLocationTimestamp > 5000;
      if (!lastLocationTimestamp || stale) {
        api.getWorkerLocationFromJobApp(professionalId, jobId).then((loc) => {
          if (loc?.coordinates) {
            setLocation({
              lat: loc.coordinates[1],
              lng: loc.coordinates[0],
            });
          }
        });
      }
    }, 6000);

    return () => clearInterval(fallbackTimeout);
  }, [lastLocationTimestamp, professionalId, jobId, isWorker]);

  useEffect(() => {
    if (usePageBroadcast) {
      setSocketStatus(workerPositionProp ? "connected" : "disconnected");
      return;
    }

    const handleConnect = () => {
      setSocketStatus("connected");
      socket.emit("joinRoom", room);
    };

    const handleDisconnect = () => {
      setSocketStatus("disconnected");
    };

    const handleReconnectAttempt = (attempt: number) => {
      setSocketStatus(`reconnecting (attempt ${attempt})`);
    };

    const handleReconnectFailed = () => {
      setSocketStatus("failed");
    };

    const handleProfessionalLocation = ({ location: loc }: { location: Location }) => {
      if (!isWorker) {
        setLocation(loc);
        hasLiveLocationRef.current = true;
        setLastLocationTimestamp(Date.now());
      }
    };

    const handleInitialLocation = ({ location: loc }: { location: Location }) => {
      if (!isWorker && !hasLiveLocationRef.current) {
        setLocation(loc);
      }
    };

    const handleError = (err: Error) => {
      console.error("Socket error:", err);
      setSocketStatus("error");
    };

    socket.connect();

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("reconnect_attempt", handleReconnectAttempt);
    socket.on("reconnect_failed", handleReconnectFailed);
    socket.on("connect_error", handleError);
    socket.on("sendProfessionalLocation", handleProfessionalLocation);
    socket.on("sendClientLocation", () => {});
    socket.on("workerInitialLocation", handleInitialLocation);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("reconnect_attempt", handleReconnectAttempt);
      socket.off("reconnect_failed", handleReconnectFailed);
      socket.off("connect_error", handleError);
      socket.off("sendProfessionalLocation", handleProfessionalLocation);
      socket.off("sendClientLocation", () => {});
      socket.off("workerInitialLocation", handleInitialLocation);
      socket.emit("leaveRoom", room);
      socket.disconnect();
      initialWorkerLocationSentRef.current = false;
    };
  }, [room, isWorker, socket, usePageBroadcast, workerPositionProp]);

  // Worker: broadcast over socket (skipped when page-level hook handles it)
  useEffect(() => {
    if (usePageBroadcast || !isWorker || socketStatus !== "connected" || !myLocation) return;

    const emitLocation = () => {
      socket.emit("getProfessionalLocation", { room, location: myLocation });
      if (!initialWorkerLocationSentRef.current) {
        socket.emit("initialWorkerLocation", { room, location: myLocation });
        initialWorkerLocationSentRef.current = true;
      }
    };

    emitLocation();
    const interval = setInterval(emitLocation, 5000);
    return () => clearInterval(interval);
  }, [usePageBroadcast, isWorker, socketStatus, myLocation, room, socket]);

  // Client: share static job location as client pin
  useEffect(() => {
    if (isWorker || socketStatus !== "connected" || !jobPoint) return;

    setMyLocation(jobPoint);
    setotherLocationimg("/images/worker_.png");
    setMyLocationimg("/images/house.png");

    const emitClientLocation = () => {
      socket.emit("getClientLocation", { room, location: jobPoint });
    };

    emitClientLocation();
    const interval = setInterval(emitClientLocation, 5000);
    return () => clearInterval(interval);
  }, [isWorker, socketStatus, jobPoint?.lat, jobPoint?.lng, room, socket]);

  return (
    <div style={{ paddingBottom: 100 }}>
      <div style={{ padding: 20, marginBottom: 30, width: "fit-content" }}>
        <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 5 }}>
          <p style={{ fontWeight: 700, marginRight: 10 }}>{t("connectionStatus")}:</p>
          <p>
            {socketStatus === "connected"
              ? `🟢 ${t("connected")}`
              : socketStatus.startsWith("reconnecting")
                ? `🟢 ${t("reconnecting")}`
                : `🟢 ${t("disconnected")}`}
          </p>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 5 }}>
          <p style={{ fontWeight: 700, marginRight: 10 }}>{t("myStatus")}:</p>
          <p>
            {myLocation
              ? `🟢 ${t("sharingLocation")}`
              : isWorker
                ? `🔴 ${t("gettingYourLocation")}`
                : `🔴 ${t("notSharingLocations")}`}
          </p>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          <p style={{ fontWeight: 700, marginRight: 10 }}>
            {isWorker ? t("clientStatus") : t("professionalStatus")}:
          </p>
          <p>
            {location ? `🟢 ${t("sharingLocation")}` : `🔴 ${t("notSharingLocations")}`}
          </p>
        </div>
        {isWorker && distanceMeters != null && (
          <div style={{ display: "flex", flexWrap: "wrap", marginTop: 5 }}>
            <p style={{ fontWeight: 700, marginRight: 10 }}>
              {t("distanceToJob", { distance: formatDistanceMeters(distanceMeters) })}
            </p>
          </div>
        )}
        {isWorker && autoArrivalEnabled && (
          <div style={{ display: "flex", flexWrap: "wrap", marginTop: 5 }}>
            <p style={{ fontSize: 14, color: "#0d9488" }}>{t("autoArrivalEnabled")}</p>
          </div>
        )}
      </div>

      {(myLocation || location) && (
        <LiveMap
          myLocation={myLocation}
          mylocationimg={mylocationimg}
          otherlocationimg={otherlocationimg}
          otherLocation={location}
        />
      )}

      {!myLocation && !location && (
        <div style={{ padding: 20 }}>
          <p>{t("waitingForLocationData")}...</p>
        </div>
      )}
    </div>
  );
};

export default LiveLocation;
